import PropTypes from "prop-types";
import { useState } from "react";

import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { Close as CloseIcon, InfoOutlined } from "@mui/icons-material";
import Grid from "@mui/material/Grid";

import { statusColors, statusLabels } from "@/features/purchase-invoices";
import { formatDate, formatMoney } from "@/features/shared";
import { DownstreamTransactionLinks } from "@/features/ui";

export const GeneralInformationSection = ({ purchaseInvoice }) => {
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);

  const hasPayments =
    purchaseInvoice.purchasePayments &&
    purchaseInvoice.purchasePayments.length > 0;

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1">Informasi Umum</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Status
          </Typography>
          {purchaseInvoice.status && (
            <Chip
              label={statusLabels[purchaseInvoice.status]}
              color={statusColors[purchaseInvoice.status]}
              size="small"
            />
          )}
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Dibuat oleh
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.creator?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Tanggal Transaksi
          </Typography>
          <Typography variant="body1">
            {formatDate(purchaseInvoice.transactionDate)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Pemasok
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.seller?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Rekening Bank Pemasok
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.purchaseInvoiceSellerBankAccount
              ? `${purchaseInvoice.purchaseInvoiceSellerBankAccount.accountNumber} - ${purchaseInvoice.purchaseInvoiceSellerBankAccount.bank?.name ?? "-"}`
              : "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Termin Pembayaran
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.paymentTerm?.description ||
              purchaseInvoice.paymentTerm?.name ||
              "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Outlet
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.buyer?.name || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            No. Faktur
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.sellerReferenceNumber || "-"}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Subtotal
          </Typography>
          <Typography variant="body1">
            {formatMoney(purchaseInvoice.subtotal)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Pajak
          </Typography>
          <Typography variant="body1">
            {formatMoney(purchaseInvoice.taxAmount)} (
            {purchaseInvoice.taxPercentage}%)
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Total Diskon
          </Typography>
          <Typography variant="body1">
            {formatMoney(purchaseInvoice.totalDiscount)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Grand Total
          </Typography>
          <Typography variant="body1" sx={{
            fontWeight: "bold"
          }}>
            {formatMoney(purchaseInvoice.grandTotal)}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Terutang
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{
            alignItems: "center"
          }}>
            <Typography
              variant="body1"
              color={
                purchaseInvoice.outstandingAmount > 0
                  ? "error.main"
                  : "success.main"
              }
              sx={{
                fontWeight: "bold"
              }}
            >
              {formatMoney(purchaseInvoice.outstandingAmount)}
            </Typography>
            {hasPayments && (
              <IconButton
                size="small"
                onClick={() => setPaymentDialogOpen(true)}
                sx={{ color: "primary.main" }}
              >
                <InfoOutlined fontSize="small" />
              </IconButton>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Typography variant="body2" sx={{
            color: "text.secondary"
          }}>
            Catatan
          </Typography>
          <Typography variant="body1">
            {purchaseInvoice.note ?? "-"}
          </Typography>
        </Grid>
      </Grid>
      {/* Payment Details Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Stack direction="row" spacing={2} sx={{
            alignItems: "center"
          }}>
            <Typography variant="h6">Detail Pembayaran</Typography>
            <Chip
              label={`${purchaseInvoice.purchasePayments?.length || 0} Transaksi`}
              size="small"
              color="primary"
            />
          </Stack>
          <IconButton onClick={() => setPaymentDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Box>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Grand Total
                </Typography>
                <Typography variant="body1" sx={{
                  fontWeight: "bold"
                }}>
                  {formatMoney(purchaseInvoice.grandTotal)}
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="body2" sx={{
                  color: "text.secondary"
                }}>
                  Sisa Terutang
                </Typography>
                <Typography
                  variant="body1"
                  color={
                    purchaseInvoice.outstandingAmount > 0
                      ? "error.main"
                      : "success.main"
                  }
                  sx={{
                    fontWeight: "bold"
                  }}
                >
                  {formatMoney(purchaseInvoice.outstandingAmount)}
                </Typography>
              </Stack>
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  mb: 1.5
                }}>
                Daftar Pembayaran
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "grey.50",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <DownstreamTransactionLinks
                  shortReference={false}
                  items={purchaseInvoice.purchasePayments}
                />
              </Box>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  purchaseInvoice: PropTypes.object.isRequired,
};
